import { MODAL_STEPS } from "../constants/modal";
import { useIsMobile } from "../utils";
import { findBrandByDomain } from "../configs/configLoader";
import { useMediaQuery } from "react-responsive";
import { subtitlesStyles, welcomeScreenUI, lingerieUI } from "../configs";
import { isFootwear, isLingerie } from "../utils/categoryParams";

interface UsePopupStylesProps {
  font: string;
  titleStyles: {
    fontSize: string;
    textTransform?: string;
  };
  step?: { number: number } | null;
  lang: string | null;
}

export const usePopupStyles = ({
  font,
  titleStyles,
  step,
}: UsePopupStylesProps) => {
  const isMobile = useIsMobile();

  const isLargeHeightRelative = useMediaQuery({
    maxHeight: 830,
    minWidth: 767,
  });
  const isMediumHeightRelative = useMediaQuery({
    maxHeight: 678,
    minWidth: 767,
  });

  const isIOSMobile =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

  const brandDefined = findBrandByDomain();

  const brandName = brandDefined?.name;

  const isSportyAndRich = brandName === "Sporty & Rich";
  const isSRP = brandName === "SRP";
  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isVelour = brandDefined?.name === "Velour Garments";

  const isGivenchyDesktop = isGivenchy && !isMobile;
  const isGivenchyMobile = isGivenchy && isMobile;

  const isFirstIntroScreen =
    step?.number === MODAL_STEPS.INTRO.number ||
    step?.number === MODAL_STEPS.GENDER.number ||
    step?.number === MODAL_STEPS.INTRO_MOBILE.number;

  const titleTextTransform =
    (titleStyles.textTransform as React.CSSProperties["textTransform"]) || "";

  const getHeaderMarginTop = () => {
    if (
      isMobile &&
      brandName === "KENZO" &&
      step?.number === MODAL_STEPS.CONSENT.number
    ) {
      return 0;
    }

    if (isMobile) {
      return isSRP ? "20px" : "10px";
    }

    return 0;
  };

  const getHeaderMarginBottom = () => {
    if (isTDF) return "0px";
    if (
      isMobile &&
      brandName === "KENZO" &&
      step?.number === MODAL_STEPS.CONSENT.number
    ) {
      return 0;
    }

    if (!isMobile && isFootwear) {
      return "20px";
    }

    if (
      step?.number === MODAL_STEPS.RESULT.number ||
      step?.number === MODAL_STEPS.UNAVAILABLE_RESULT.number
    ) {
      return 0;
    }

    if (step?.number === MODAL_STEPS.BREASTS.number) {
      return "10px";
    }

    return "20px";
  };

  const headerStyles: React.CSSProperties = {
    marginTop: getHeaderMarginTop(),
    marginBottom: isGivenchy ? 0 : getHeaderMarginBottom(),
    width: isSRP ? "calc(100% + 50px)" : "100%",
    position: isSRP ? "relative" : "initial",
    right: isSRP ? "25px" : "0",
    bottom: isSRP ? "20px" : "0",
    backgroundColor: isSRP ? "#FFFFFF" : "inherit",
    minHeight: isGivenchy ? "20px" : isSRP ? "45px" : "auto",
    borderRadius: !isMobile && isSRP ? "8px" : 0,
    display: "flex",
    justifyContent: "space-between",
  };

  const getPopupWidth = () => {
    if (isGivenchyMobile) return "calc(100% - 40px)";
    if (isMobile) return "calc(100% - 50px)";
    if (isGivenchyDesktop) return "50%";

    return "445px";
  };

  const paddingRules = {
    GivenchyMobile: {
      top: "5px",
      bottom: "20px",
      horizontal: "20px",
    },
    GivenchyDesktop: {
      top: "40px",
      bottom: "80px",
      horizontal: "40px",
    },
    SRPMobile: {
      top: "0",
      bottom: "10px",
      horizontal: "25px",
    },
    Default: {
      top: "25px",
      bottom: "10px",
      horizontal: "25px",
    },
  };

  const rule = isGivenchyMobile
    ? paddingRules.GivenchyMobile
    : isGivenchyDesktop
      ? paddingRules.GivenchyDesktop
      : isMobile && brandName === "SRP"
        ? paddingRules.SRPMobile
        : paddingRules.Default;

  const getBorderRadius = () => {
    if (isMobile && brandName === "Victoria Beckham") return "10px 10px 0 0";
    if (!isMobile && brandName === "SRP") return "8px 0 0 8px";
    return 0;
  };

  const getMaxHeight = () => {
    if (isMobile && isSRP) return "";
    if (isMobile && !isSRP) return "calc(100dvh - 80px)";
    return "100%";
  };

  const getMinHeightMobileiOS = () => {
    const stepNum = step?.number;

    if (isMobile) {
      if (isGivenchyMobile && stepNum === MODAL_STEPS.ERROR.number)
        return "auto";

      if (isGivenchyMobile && stepNum === MODAL_STEPS.INTRO_MOBILE.number)
        return "auto";

      if (stepNum === MODAL_STEPS.GENDER.number) return "65dvh";

      if (stepNum === MODAL_STEPS.UNAVAILABLE_RESULT.number) return "95dvh";

      return "87dvh";
    }

    return "auto";
  };

  const getMinHeightBasedMobile = () => {
    const stepNum = step?.number;

    if (
      isGivenchyMobile &&
      (stepNum === MODAL_STEPS.ERROR.number ||
        stepNum === MODAL_STEPS.INTRO_MOBILE.number)
    )
      return "auto";

    if (!isMobile) return "52dvh";

    if (isTDF) return "60dvh";

    if (brandName === "KENZO") {
      if (stepNum === MODAL_STEPS.CONSENT.number) return "90dvh";
      if (stepNum === MODAL_STEPS.GENDER.number) return "60dvh";
      if (stepNum === MODAL_STEPS.INTRO_MOBILE.number) return "85dvh";
    }

    if (isVB) {
      if (
        stepNum === MODAL_STEPS.BELLY.number ||
        stepNum === MODAL_STEPS.TORSO.number ||
        stepNum === MODAL_STEPS.CUISSES.number
      )
        return "72dvh";
      if (stepNum === MODAL_STEPS.BREASTS.number) return "90dvh";
    }

    if (isVelour)
      return stepNum === MODAL_STEPS.INTRO_MOBILE.number ? "80dvh" : "60dvh";

    if (stepNum === MODAL_STEPS.GENDER.number) return "52dvh";
    if (stepNum === MODAL_STEPS.INTRO_MOBILE.number) return "72dvh";
    if (isFootwear && isFirstIntroScreen) return "62dvh";

    return "52dvh";
  };

  const popupStyles: React.CSSProperties = {
    width: getPopupWidth(),
    fontFamily: `${font}, sans-serif${!isMobile ? " !important" : ""}`,
    backgroundColor: brandName === "SRP" ? "#F7F7F7" : "#FFFFFF",
    height: "auto",
    borderRadius: getBorderRadius(),
    maxHeight: getMaxHeight(),
    minHeight: isIOSMobile
      ? getMinHeightMobileiOS()
      : getMinHeightBasedMobile(),
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    paddingTop: rule.top,
    paddingBottom: rule.bottom,
    paddingLeft: rule.horizontal,
    paddingRight: rule.horizontal,
    boxSizing: isGivenchyDesktop ? "border-box" : undefined,
    boxShadow: isGivenchy ? "none" : "0 2px 10px rgba(0, 0, 0, 0.1)",
  };

  const getTitlesStylesFontSize = () => {
    const base = welcomeScreenUI.input_fields.title.fontSize;

    if (isIntersport || isBlackstore || isTDF || isGivenchy) return base;

    const needSmaller =
      (isLargeHeightRelative && !isSportyAndRich) ||
      (isMediumHeightRelative && isSportyAndRich);

    if (needSmaller) return `calc(${base} - 2px)`;

    if (isMobile && !isGivenchy) return "14px";

    return base;
  };

  const titlesStyles: React.CSSProperties = {
    fontWeight: welcomeScreenUI.input_fields.title.fontWeight,
    textAlign: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["textAlign"],
    textTransform: welcomeScreenUI.input_fields.title
      .textTransform as React.CSSProperties["textTransform"],
    color: welcomeScreenUI.input_fields.title
      .fontColor as React.CSSProperties["color"],
    fontSize: getTitlesStylesFontSize(),
    justifyContent: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["justifyContent"],
    letterSpacing: (welcomeScreenUI.input_fields.title as any)?.letterSpacing,
    fontStyle: (welcomeScreenUI.input_fields.title as any)
      .fontStyle as React.CSSProperties["fontStyle"],
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
  };

  const subtitleConfigStyles: React.CSSProperties = {
    color: subtitlesStyles.color as React.CSSProperties["color"],
    fontSize: subtitlesStyles.fontSize as React.CSSProperties["fontSize"],
    fontWeight: subtitlesStyles.fontWeight as React.CSSProperties["fontWeight"],
    textAlign: subtitlesStyles.textAlign as React.CSSProperties["textAlign"],
    textTransform:
      subtitlesStyles.textTransform as React.CSSProperties["textTransform"],
    justifyContent:
      subtitlesStyles.textAlign as React.CSSProperties["justifyContent"],
    fontFamily: isMobile
      ? `${font}, sans-serif`
      : `${font}, sans-serif !important`,
    letterSpacing: (subtitlesStyles as any)?.letterSpacing,
  };

  const getDescriptionFontSize = () => {
    const base = isLingerie && lingerieUI.subtitles?.fontSize
      ? lingerieUI.subtitles.fontSize
      : subtitleConfigStyles.fontSize;

    if (isIntersport || isBlackstore || isSRP || isTDF || isGivenchy)
      return base;

    const shouldReduce =
      (isLargeHeightRelative && !isSportyAndRich) ||
      (isMediumHeightRelative && isSportyAndRich);

    return shouldReduce ? `calc(${base} - 2px)` : base;
  };

  const getDescriptionTextTransform = () => {
    return subtitleConfigStyles.textTransform === "capitalize"
      ? "none"
      : subtitlesStyles.textTransform;
  };

  const getDescriptionMarginTop = () => {
    if (isGivenchy && !isMobile) return "10px";
    if (isSRP) return "40px";

    const isAdaptive =
      (isLargeHeightRelative && !isSportyAndRich) ||
      (isMediumHeightRelative && isSportyAndRich);

    if (isAdaptive) return "20px";
    if (isTDF) return "15px";

    return "10px";
  };

  const getDescriptionMarginBottom = () => {
    const isAdaptive =
      (isLargeHeightRelative && !isSportyAndRich) ||
      (isMediumHeightRelative && isSportyAndRich);

    if (isGivenchy && isMobile) return "10px";
    if (isAdaptive) return "20px";
    if (!isMobile && !isTDF) return "40px";
    if (isTDF && !isMobile) return "20px";
    if (isTDF && isMobile) return "20px";

    return "30px";
  };

  const descriptionStyles: React.CSSProperties = {
    ...subtitleConfigStyles,
    fontSize: getDescriptionFontSize(),
    textTransform: getDescriptionTextTransform(),
    marginTop: getDescriptionMarginTop(),
    marginBottom: getDescriptionMarginBottom(),
    letterSpacing: (subtitlesStyles as any)?.letterSpacing,
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
  };

  return {
    titlesStyles,
    descriptionStyles,
    titleTextTransform,
    headerStyles,
    popupStyles,
  };
};
